import { inject, Injectable, signal, Signal } from '@angular/core';
import {
  gcButtonType,
  GcButtonType,
  GcTableColumn,
  GcTableRow,
  GcTextColor,
  gcTextColors,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import {
  ExportButtonStatus,
  SharepointExportItemStatus,
  SharepointExportResponse,
} from '../entities/sharepoint-export-entities';

@Injectable({
  providedIn: 'root',
})
export class SharepointExportTableService {
  private readonly translate = inject(IpoTranslatePipe);

  private readonly tableColumnData: GcTableColumn[] = [
    { columnKey: 'reportType', sortable: true },
    { columnKey: 'totalAmount' },
    { columnKey: 'readyToExport' },
    { columnKey: 'status', cellTemplate: true },
    { columnKey: 'inProgress' },
    { columnKey: 'exported' },
    { columnKey: 'failed', cellTemplate: true },
    { columnKey: 'exportButton', cellTemplate: true },
  ];

  getTableColumns(): Signal<GcTableColumn[]> {
    const columns = signal<GcTableColumn[]>(this.createTableColumns());
    return columns.asReadonly();
  }

  getTableRowData(data: SharepointExportResponse): GcTableRow[] {
    return this.mapTableRows(data);
  }

  private createTableColumns(): GcTableColumn[] {
    return this.tableColumnData.map((column) => {
      if (column.columnKey === 'exportButton') {
        return column;
      }
      return {
        ...column,
        headerText: this.translateColumnHeader(column.columnKey),
        alignment: 'left',
      };
    });
  }

  private translateColumnHeader(columnKey: string): string {
    return this.translate.transform(
      'sharepointExport.table.columns.' + columnKey,
    );
  }

  private mapTableRows(data: SharepointExportResponse): GcTableRow[] {
    if (!data.length) {
      return [];
    }
    const mapppedTableRowData = data.map((item) => {
      return {
        ...item,
        reportType: this.translate.transform(
          `sharepointExport.table.reportType.${item.reportType}`,
        ),
        status: {
          copy: this.translate.transform(
            `sharepointExport.table.status.${item.status}`,
          ),
          textColor: this.getStatusStyling(item.status),
        },
        inProgress: item.inProgress || ' ',
        exported:
          item.status !== 'IN_PROGRESS' && item.exported === 0
            ? ' '
            : item.exported,
        failed:
          item.status !== 'IN_PROGRESS' && item.failed === 0
            ? ' '
            : item.failed,
        exportButton: {
          ...item.exportButton,
          styling: this.getExportButtonStyling(item.exportButton.status),
          copy: this.getExportButtonCopy(
            item.exportButton.status,
            item.exportButton.amount,
          ),
          reportType: item.reportType,
        },
      } as GcTableRow;
    });
    return mapppedTableRowData;
  }

  private getStatusStyling(status: SharepointExportItemStatus): GcTextColor {
    switch (status) {
      case SharepointExportItemStatus.COMPLETED: {
        return gcTextColors.success;
      }
      case SharepointExportItemStatus.IN_PROGRESS: {
        return gcTextColors.active;
      }
      default: {
        return gcTextColors.primary;
      }
    }
  }

  private getExportButtonStyling(status: ExportButtonStatus): {
    type: GcButtonType;
    disabled: boolean;
  } {
    switch (status) {
      case ExportButtonStatus.EXPORTING: {
        return { type: gcButtonType.primary, disabled: true };
      }
      case ExportButtonStatus.NOT_READY: {
        return { type: gcButtonType.secondary, disabled: true };
      }
      case ExportButtonStatus.ALL_READY: {
        return { type: gcButtonType.primary, disabled: false };
      }
      default: {
        return { type: gcButtonType.secondary, disabled: false };
      }
    }
  }

  private getExportButtonCopy(
    status: ExportButtonStatus,
    amount: number,
  ): string {
    const baseCopy: string = this.translate.transform(
      `sharepointExport.table.exportButton.${status}`,
    );
    return amount > 0 ? `${baseCopy} (${amount})` : baseCopy;
  }
}
